<script lang="ts" setup>
import { AppButton, AppOTP } from '#core/components';
import type { FormErrors } from '#core/types/Form';
import { computed } from 'vue';
import type { ForgotPasswordVerifyForm } from '../../types/Auth';

const { validations } = useValidation();

const { username = '' } = defineProps<{
  username: string;
}>();

const { form, handleSubmit, getErrors } = useForm<ForgotPasswordVerifyForm>({
  form: {
    username: username,
    token: '',
  },
  rules: {
    token: {
      required: validations.required('OTP'),
      minLength: validations.minLength('OTP', 5),
    },
  },
});

const {
  data,
  pending: loadingVerify,
  execute: execVerify,
  error: serverError,
} = useSubmit<{ data: void }, { data: FormErrors<ForgotPasswordVerifyForm> }>();
loadingVerify.value = false;

const emits = defineEmits<{
  (e: 'next', token: string): void;
}>();

const handleSignUp = async (): Promise<void> => {
  handleSubmit(async () => {
    await execVerify('/auth/reset-password/check', {
      method: 'POST',
      body: form.value,
    });

    if (!serverError.value) {
      emits('next', form.value.token);
    }
  });
};

const {
  handleForgotPassword,
  form: forgotPasswordForm,
  isForgotPasswordSuccess,
  loadingForgotPassword,
} = useForgotPassword();

watch(
  () => username,
  () => {
    forgotPasswordForm.value.username = username;
  },
  {
    immediate: true,
  }
);

const errors = computed(() => {
  return getErrors({ token: serverError.value?.data?.errors?.token });
});

const canContinue = computed(() => {
  return form.value.token.trim().length >= 5;
});
</script>

<template>
  <v-form @submit.prevent="handleSignUp">
    <AppOTP
      v-model="form.token"
      :disabled="loadingVerify"
      :error-messages="errors.token"
      data-testid="otp-input"
      :is-resend-success="isForgotPasswordSuccess"
      :resend-loading="loadingForgotPassword"
      @resend="handleForgotPassword"
    />

    <AppButton
      data-testid="continue"
      block
      data-cy="continue"
      class="mt-8 font-weight-bold"
      :loading="loadingVerify"
      :disabled="!canContinue || loadingForgotPassword || loadingVerify"
      >Continue</AppButton
    >
  </v-form>
</template>

<style lang="scss" scoped>
.v-card {
  margin-top: 132px;
}
</style>
